import { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { brandsService } from 'services/brand.service'

import { _object } from 'utils/interfaces'

import { Button, InputField } from '../form-inputs'
import { toast } from 'react-toastify'
import store from 'store'

const AddNewBrandModal = () => {
  const [loading, setLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const validationSchema = Yup.object({
    title: Yup.string().label('Title').required()
  })

  const formik: any = useFormik({
    initialValues: {
      title: '',
      description: ''
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      setLoading(true)
      brandsService.addBrand(values).then((results: _object) => {
        setLoading(false)
        if (results?.error === false) {
          // success toast
          toast.success(results.message || 'Brand added successfully.', { autoClose: 3000 })

          // reset form state
          formik.resetForm()
          setSelectedFile(null)

          const closeModal = () => {
            const modalEl = document.getElementById('add-new-brand')
            if (modalEl) {
              modalEl.classList.add('hidden')
            }
          }

          // If there's no image selected, we dispatch an event so the list can refresh
          if (!selectedFile) {
            window.dispatchEvent(new Event('brandAdded'))
            closeModal()
            return
          }

          setIsUploading(true)

          const uploadImage = async () => {
            const data = new FormData()
            if (results.data.id) {
              data.append('entity_id', results.data.id)
            }
            data.append('entity_type', 'brand')
            data.append('key', 'brand_image')
            data.append('files', selectedFile as File)
            const accessToken = store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || '')

            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/media`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                Authorization: accessToken ? accessToken : ''
              },
              body: data
            })

            const result = await response.json()
            if (result.error === false) {
              toast.success(result.message || 'Image uploaded successfully.', {
                autoClose: 3000
              })
              // Signal that a brand was added/updated so parent list can refresh
              window.dispatchEvent(new Event('brandAdded'))
              closeModal()
            } else if (result.error === true) {
              toast.error(result.message || 'Image not uploaded.', {
                autoClose: 3000
              })
            }
            setIsUploading(false)
          }

          uploadImage()
        } else if (results?.error === true) {
          toast.error(results.message || 'Failed to add brand.', { autoClose: 3000 })
        }
      })
    }
  })
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null
    if (file) {
      setSelectedFile(file)
    }
  }
  return (
    <>
      <div
        id="add-new-brand"
        tabIndex={-1}
        aria-hidden="true"
        className="fixed top-0 right-0 left-0 z-50 flex hidden h-[calc(100%-1rem)] max-h-full w-full items-center justify-center overflow-x-hidden overflow-y-auto md:inset-0"
      >
        <div className="relative max-h-full w-full max-w-2xl p-4">
          <div className="relative z-10 max-h-full w-full max-w-2xl p-4">
            <div className="relative rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between rounded-t border-b border-gray-200 p-3 px-5">
                <h3 className="text-lg font-semibold">Add New Brand</h3>
                <button
                  type="button"
                  className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400"
                  data-modal-hide="add-new-brand"
                >
                  <svg
                    className="h-3 w-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <form className="p-5" onSubmit={formik.handleSubmit}>
                <InputField
                  label="Brand Title"
                  name="title"
                  type="text"
                  placeholder="Brand Title"
                  onChange={formik.handleChange}
                  value={formik.values.title}
                  onBlur={formik.handleBlur}
                  required={true}
                  error={formik.touched.title && formik.errors.title}
                />

                <div className="mb-5">
                  <label
                    htmlFor="description"
                    className="mb-2 flex text-sm leading-none font-medium text-gray-700"
                  >
                    Brand Description
                  </label>

                  <textarea
                    id="description"
                    name="description"
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    rows={4}
                    className="w-full rounded-md border border-transparent bg-gray-100 px-3 py-2 text-black placeholder-gray-500 focus:border-gray-400 focus:outline-none"
                    placeholder="Brand Description"
                  ></textarea>
                </div>
                <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                  Brand Image <span className="text-red-500">*</span>
                </label>
                <h3 className="mb-2 mb-2 text-[10px] text-[#969494] font-medium">275W × 183H | 3:2</h3>
                <div className="flex items-center gap-4">
                  <label htmlFor="imageUpload">
                    <div className="relative flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-purple-900">
                      <input
                        type="file"
                        id="imageUpload"
                        accept=".png, .jpg, .jpeg"
                        onChange={handleImageChange}
                        className="absolute inset-0 z-10 cursor-pointer opacity-0"
                      />
                      {isUploading ? (
                        <svg
                          className="h-8 w-8 animate-spin text-purple-900"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      ) : (
                        <span className="text-4xl font-bold text-purple-900">+</span>
                      )}
                    </div>
                  </label>
                  {selectedFile && (
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="h-32 w-40 rounded-md border object-cover"
                    />
                  )}
                </div>
                <div className="mt-3">
                  <Button
                    label="Add"
                    type="submit"
                    className={`rounded-lg bg-purple-900 py-2 text-center text-sm text-white transition hover:bg-purple-950  ${loading === true && 'opacity-50 hover:bg-purple-950 is-loading'}`}
                    // loading={loading}
                    disabled={!formik.isValid}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default AddNewBrandModal
